import { ConfirmDialog } from "@/components/ConfirmDialog";
import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { GithubIcon } from "@/components/GithubIcon";
import { instanceLabel, repoNoun, useCodeHosts } from "@/lib/code-host";
import { SelectedReposWarning } from "./AppSourceDefault";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ApiError,
  api,
  type BundleCatalogEntry,
  type BundleInput,
  type BundleInstallOptions,
  type BundleInstallStatus,
  type BundleSource,
  type DeployPlatform,
  type Permissions,
} from "@/lib/api";
import {
  addressAttaching,
  addressPending,
  alwaysOn,
  capabilityOn,
  categoryLabel,
  countPendingUpdates,
  dependentsOn,
  INSTALL_IN_FLIGHT,
  isCoreBundle,
  notOnBooYet,
  ON_BOO,
  orderCategories,
  listPoll,
  plural,
  reconcileAnnouncement,
  rowAction,
  settleAddresses,
  statusMessage,
  STORE_REMOVE_VERB,
  turnOffBody,
  updateMarker,
  versionRows,
  type RowAction,
} from "@/lib/bundle-state";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Check, ChevronLeft, ChevronRight, Clock, Cloud, ExternalLink, Loader2, Lock, RotateCw, Settings, TriangleAlert, Wrench } from "lucide-react";
import { Children, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";

// Background provisioning is coarse: poll while a bundle (or config) is
// mid-flight, stop once it lands in a terminal state.
const POLL_MS = 3500;
// A bundle that retries later or waits on something changes on its own, slowly.
const RECONCILE_WAIT_POLL_MS = 30_000;

// Brain isn't a running brain in this org (404) or isn't running yet (409).
// Both mean "can't act on bundles right now": keep prior state, hide Install.
const NOT_READY_CODES = new Set(["brain_not_ready", "not_found"]);
const isNotReady = (err: unknown) => err instanceof ApiError && NOT_READY_CODES.has(err.code);

// Bundle icons are clay tiles cut flush to the edge with transparent ~22%
// rounded corners, so the box gets no padding, background, or border. The
// fallback tile keeps the same box so rows line up with or without an icon.
const ICON_RADIUS = "22%";
const FALLBACK_GRADIENT = "linear-gradient(135deg, #06b6d4, #8b5cf6, #ec4899)";

function BundleIcon({ bundle, size = 40 }: { bundle: BundleCatalogEntry; size?: number }) {
  const icons = bundle.icons;
  const src = icons[0]?.src;
  // Keyed by src so a refetch with a new URL retries instead of staying on the fallback.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  if (!src || failedSrc === src) {
    return (
      <div
        aria-hidden
        className="shrink-0 flex items-center justify-center text-white font-semibold select-none"
        style={{
          width: size,
          height: size,
          borderRadius: ICON_RADIUS,
          backgroundImage: FALLBACK_GRADIENT,
          fontSize: size * 0.45,
        }}
      >
        {[...bundle.name.trim()][0]?.toUpperCase() ?? ""}
      </div>
    );
  }

  return (
    <img
      src={src}
      srcSet={icons.map((i) => `${i.src} ${i.size}w`).join(", ")}
      sizes={`${size}px`}
      width={size}
      height={size}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => setFailedSrc(src)}
      // The tiles' light edges vanish on a white card without a hairline.
      className="shrink-0 outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/10"
      style={{ width: size, height: size, borderRadius: ICON_RADIUS }}
    />
  );
}

// Fixed min width so the label swap (Enable → Enabling…) doesn't shift the
// row; the pseudo-element grows the 32px button to a 40px hit area.
const ACTION_CLS = "relative min-w-20 rounded-full px-3.5 after:absolute after:-inset-1";

function ActionButton({
  action,
  canInstall,
  blocked,
  prominent = false,
  onEnable,
  onUpdate,
  onRetry,
  onAddDetails,
}: {
  action: RowAction;
  canInstall: boolean;
  blocked: boolean; // another operation on this Boo must finish first
  prominent?: boolean; // the detail sheet, where Enable/Update is the one decision
  onEnable: () => void;
  onUpdate: () => void;
  onRetry: () => void; // Retry or Repair on a Boo that reconciles its bundles
  onAddDetails: () => void; // the inputs a reconciled bundle waits on
}) {
  const decide = prominent ? "default" : "secondary";
  switch (action.kind) {
    case "busy":
      return (
        <Button variant="secondary" className={ACTION_CLS} disabled>
          <Loader2 className="size-3.5 animate-spin" />
          {action.label}
        </Button>
      );
    case "enabled":
      return (
        <span className="inline-flex min-w-20 items-center justify-center gap-1 text-xs text-muted-foreground">
          <Check className="size-3.5" />
          Enabled
        </span>
      );
    case "enable":
      return canInstall ? (
        <Button variant={decide} className={ACTION_CLS} onClick={onEnable} disabled={blocked}>
          Enable
        </Button>
      ) : null;
    case "retry-install":
      return canInstall ? (
        <Button variant="outline" className={ACTION_CLS} onClick={onEnable} disabled={blocked}>
          <RotateCw className="size-3.5" />
          Retry
        </Button>
      ) : null;
    case "update":
      return canInstall ? (
        <Button variant={decide} className={ACTION_CLS} onClick={onUpdate} disabled={blocked}>
          {prominent && action.version ? `Update to ${action.version}` : "Update"}
        </Button>
      ) : null;
    case "retry-update":
      return canInstall ? (
        <Button variant="outline" className={ACTION_CLS} onClick={onUpdate} disabled={blocked}>
          <RotateCw className="size-3.5" />
          {prominent && action.version ? `Retry update to ${action.version}` : "Retry update"}
        </Button>
      ) : null;
    case "retry":
      return canInstall ? (
        <Button variant="outline" className={ACTION_CLS} onClick={onRetry} disabled={blocked}>
          <RotateCw className="size-3.5" />
          Retry
        </Button>
      ) : null;
    case "repair":
      return canInstall ? (
        <Button variant={decide} className={ACTION_CLS} onClick={onRetry} disabled={blocked}>
          <Wrench className="size-3.5" />
          Repair
        </Button>
      ) : null;
    case "waiting":
      return (
        <span className="inline-flex min-w-20 items-center justify-center gap-1 text-xs text-muted-foreground">
          <Clock className="size-3.5" />
          Waiting
        </span>
      );
    case "add-details":
      return canInstall ? (
        <Button variant={decide} className={ACTION_CLS} onClick={onAddDetails} disabled={blocked}>
          Add details
        </Button>
      ) : null;
  }
}

function StatusLine({ bundle, full = false }: { bundle: BundleCatalogEntry; full?: boolean }) {
  const msg = statusMessage(bundle);
  if (!msg) return null;
  return (
    <span
      className={cn(
        "block text-xs",
        full ? "text-pretty" : "truncate",
        msg.tone === "error" ? "text-destructive" : "text-muted-foreground",
      )}
    >
      {msg.text}
    </span>
  );
}

const MARKER_DOT = { update: "bg-sky-500", failed: "bg-destructive", confirming: "bg-amber-500" } as const;

// Pending-update state beside an installed bundle's name. Updating happens in
// the detail sheet; the row only says that there is one.
function UpdateMarker({ bundle }: { bundle: BundleCatalogEntry }) {
  const marker = updateMarker(bundle);
  if (!marker) return null;
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-muted-foreground">
      <span aria-hidden className={cn("size-1.5 rounded-full", MARKER_DOT[marker.tone])} />
      {marker.label}
    </span>
  );
}

// Icon-only row control: 32px visible, 40px hit area, label in a tooltip.
function RowIconButton({ label, tooltip, children }: { label: string; tooltip: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={label}
          className="relative rounded-full text-muted-foreground after:absolute after:-inset-1"
          asChild
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

// An installed bundle: copy comes from the backend verbatim (BOO-198). The
// gear (and the row itself) opens the detail sheet, where it is managed;
// `trailing` adds an app's ↗ link or a running operation's progress.
function BundleRow({
  bundle,
  trailing,
  note,
  onOpenDetails,
}: {
  bundle: BundleCatalogEntry;
  trailing: ReactNode;
  note?: ReactNode; // a line under the name, outside the row's button
  onOpenDetails: () => void;
}) {
  return (
    <li className="px-4 py-3">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onOpenDetails}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-md text-left outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          <BundleIcon bundle={bundle} />
          <span className="min-w-0 flex-1">
            {/* Wraps so a narrow row keeps the whole name and drops the marker below it. */}
            <span className="flex min-w-0 flex-wrap items-center gap-x-2">
              <span className="max-w-full truncate text-sm font-medium">{bundle.name}</span>
              <UpdateMarker bundle={bundle} />
            </span>
            <span className="block truncate text-xs text-muted-foreground">{bundle.tagline}</span>
          </span>
        </button>
        <div className="flex shrink-0 items-center gap-1">
          {trailing}
          <RowIconButton label={`Manage ${bundle.name}`} tooltip="Manage">
            <button type="button" onClick={onOpenDetails}>
              <Settings className="size-4" />
            </button>
          </RowIconButton>
        </div>
      </div>
      {/* Lines up under the name: 40px icon + 12px gap. */}
      {note && <div className="pl-[52px]">{note}</div>}
    </li>
  );
}

function BundleSection({
  title,
  aside,
  toolbar,
  layout,
  children,
}: {
  title: string;
  aside?: ReactNode; // beside the title
  toolbar?: ReactNode; // its own full-width line under the title
  layout: "list" | "grid";
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
        {aside}
      </div>
      {toolbar}
      <ul
        className={
          layout === "list" ? "divide-y rounded-xl border bg-card" : "grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
        }
      >
        {children}
      </ul>
    </section>
  );
}

// One line of chips that scrolls sideways however many there are, with a fade
// on each edge that has more chips past it. From sm up an arrow button sits on
// that edge too, since a mouse wheel scrolls the page, not the row; phones swipe.
function ChipScroller({ label, children }: { label: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });
  const count = Children.count(children);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const start = el.scrollLeft > 1;
      const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const resize = new ResizeObserver(update);
    resize.observe(el);
    for (const child of el.children) resize.observe(child);
    return () => {
      el.removeEventListener("scroll", update);
      resize.disconnect();
    };
  }, [count]);
  const fade = 24;
  const mask =
    edges.start || edges.end
      ? `linear-gradient(to right, ${edges.start ? "transparent" : "black"}, black ${fade}px, black calc(100% - ${fade}px), ${edges.end ? "transparent" : "black"})`
      : undefined;
  const page = (dir: -1 | 1) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.75, behavior: "smooth" });
  };
  const arrow = "absolute top-1/2 z-10 hidden size-8 -translate-y-1/2 rounded-full bg-background shadow-sm ring-1 ring-foreground/10 sm:inline-flex";
  return (
    <div className="relative">
      <div
        ref={ref}
        role="group"
        aria-label={label}
        className="-mx-1 flex gap-1.5 overflow-x-auto px-1 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ maskImage: mask, WebkitMaskImage: mask }}
      >
        {children}
      </div>
      {edges.start && (
        <Button variant="ghost" size="icon" tabIndex={-1} aria-label="Scroll categories left" className={cn(arrow, "left-0")} onClick={() => page(-1)}>
          <ChevronLeft className="size-4" />
        </Button>
      )}
      {edges.end && (
        <Button variant="ghost" size="icon" tabIndex={-1} aria-label="Scroll categories right" className={cn(arrow, "right-0")} onClick={() => page(1)}>
          <ChevronRight className="size-4" />
        </Button>
      )}
    </div>
  );
}

// A catalog tile for something not yet on this Boo. No button: enabling an app
// is a commitment (setup, inputs, real infrastructure), so the decision happens
// in the detail sheet with the full description in view.
function BundleTile({
  bundle,
  pendingLabel,
  onOpenDetails,
}: {
  bundle: BundleCatalogEntry;
  pendingLabel: string | null;
  onOpenDetails: () => void;
}) {
  const action = rowAction(bundle, pendingLabel);
  // Screen readers announce the name as the tile's label, the rest as its description.
  const id = useId();
  return (
    <li className="flex">
      <button
        type="button"
        onClick={onOpenDetails}
        aria-labelledby={`${id}-name`}
        aria-describedby={`${id}-details`}
        className={cn(
          // One column on phones reads as a list (icon beside text); tiles stack from sm up.
          "flex w-full items-start gap-3 rounded-2xl border bg-card p-4 text-left outline-none sm:flex-col",
          "transition-[border-color,box-shadow,scale] duration-150 ease-out active:scale-[0.99]",
          "hover:border-foreground/15 hover:shadow-[0_1px_2px_-1px_rgba(0,0,0,0.08),0_4px_12px_-2px_rgba(0,0,0,0.08)]",
          "dark:hover:border-white/20 dark:hover:shadow-none",
          "focus-visible:ring-3 focus-visible:ring-ring/50",
        )}
      >
        <BundleIcon bundle={bundle} size={48} />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5 self-stretch">
          <span id={`${id}-name`} className="truncate text-sm font-medium">
            {bundle.name}
          </span>
          <span id={`${id}-details`} className="flex flex-1 flex-col">
            <span className="line-clamp-2 text-xs text-muted-foreground text-pretty">{bundle.tagline}</span>
            {action.kind === "busy" ? (
              <span className="mt-auto inline-flex items-center gap-1.5 pt-2 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" />
                {action.label}
              </span>
            ) : (
              <span className="mt-auto pt-2 empty:hidden">
                <StatusLine bundle={bundle} />
              </span>
            )}
          </span>
        </span>
      </button>
    </li>
  );
}

function BundleSheet({
  bundle,
  action,
  canUninstall,
  blocked,
  onDisable,
  onClose,
  addressNote,
}: {
  bundle: BundleCatalogEntry;
  action: ReactNode;
  canUninstall: boolean;
  blocked: boolean;
  onDisable: () => void;
  onClose: () => void;
  addressNote?: ReactNode; // shown instead of the address until it is attached
}) {
  const { status, url, repository, upgrade } = bundle.install;
  // "owner/name" is a GitHub repository; a URL is the project page on whatever
  // host the brain recorded (GitLab). Anything else isn't linked.
  const repoUrl = repository && /^https?:\/\//.test(repository) ? repository.replace(/\.git$/, "") : null;
  const repoLabel = repoUrl ? repoUrl.replace(/^https?:\/\/[^/]+\//, "") : (repository?.replace(/\.git$/, "") ?? null);
  const repoHref = repoUrl ?? (repoLabel && /^[A-Za-z0-9-]+\/[A-Za-z0-9._-]+$/.test(repoLabel) ? `https://github.com/${repoLabel}` : null);
  const versions = versionRows(bundle);
  const installed = status === "installed";
  // Older brains have no tagline; the backend's fallback is the description's
  // first sentence, which the sheet would otherwise show twice.
  const taglineRepeats = bundle.description.startsWith(bundle.tagline);

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      {/* No autofocus: it would land on Enable/Update, where Enter starts it. */}
      <SheetContent className="w-full gap-0 overflow-y-auto sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
        <SheetHeader className="gap-4 p-6 pb-5">
          <BundleIcon bundle={bundle} size={64} />
          <div className="space-y-1">
            <SheetTitle className="text-lg text-balance">{bundle.name}</SheetTitle>
            <SheetDescription className={cn("text-pretty", taglineRepeats && "sr-only")}>{bundle.tagline}</SheetDescription>
          </div>
          {action && <div className="flex items-center">{action}</div>}
          <StatusLine bundle={bundle} full />
        </SheetHeader>

        <div className="flex flex-col gap-5 px-6 pb-6">
          <p className="text-sm text-pretty">{bundle.description}</p>

          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Type</dt>
            <dd>{bundle.kind === "app" ? "App, runs at its own address" : "Skill, adds to what your Boo can do"}</dd>
            {bundle.category && (
              <>
                <dt className="text-muted-foreground">Category</dt>
                <dd>{categoryLabel(bundle.category)}</dd>
              </>
            )}
            {versions.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="tabular-nums">{value}</dd>
              </div>
            ))}
            {installed && addressNote && (
              <>
                <dt className="text-muted-foreground">Address</dt>
                <dd className="min-w-0">{addressNote}</dd>
              </>
            )}
            {installed && url && !addressNote && (
              <>
                <dt className="text-muted-foreground">Address</dt>
                <dd className="min-w-0">
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex max-w-full items-center gap-1 underline underline-offset-2 hover:text-foreground"
                  >
                    <span className="truncate">{url.replace(/^https?:\/\//, "")}</span>
                    <ExternalLink className="size-3 shrink-0" />
                  </a>
                </dd>
              </>
            )}
            {installed && repoHref && (
              <>
                <dt className="text-muted-foreground">Source</dt>
                <dd className="min-w-0">
                  <a
                    href={repoHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex max-w-full items-center gap-1.5 underline underline-offset-2 hover:text-foreground"
                  >
                    <GithubIcon className="size-3.5 shrink-0" />
                    <span className="truncate">{repoLabel}</span>
                  </a>
                </dd>
              </>
            )}
          </dl>

          {installed && !upgrade.available && upgrade.conflicts.length > 0 && (
            <div className="space-y-1.5 text-sm">
              <p className="text-muted-foreground">Boo is reconciling these customized areas with the new version:</p>
              <ul className="list-disc space-y-0.5 pl-5">
                {upgrade.conflicts.map((c) => (
                  <li key={c} className="break-words">
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* An always-on capability can't be removed, so staff get no button for it either. */}
        {(installed || bundle.install.reconcile?.desired === "present") && canUninstall && !alwaysOn(bundle) && (
          <SheetFooter className="border-t">
            <Button variant="ghost" className="justify-start text-destructive hover:text-destructive" onClick={onDisable} disabled={blocked}>
              {STORE_REMOVE_VERB} {bundle.name}
            </Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}

// GitHub's repository name rules.
const REPO_NAME_RE = /^[A-Za-z0-9._-]{1,100}$/;

type SourceChoice = {
  host: "github" | "gitlab";
  hostLabel: string;
  instance: string | null; // self-managed hostname, null for github.com / gitlab.com
  connectionId: string;
  accountLogin: string;
  defaultSource: BundleSource;
  selectedOnly: boolean;
  manageUrl: string | null;
};

// Collects what an install or update needs before it can run: where the app
// runs (a bundle supporting more than one configured deploy platform), where
// its source lives (an app bundle on a Boo connected to GitHub), and the
// bundle's inputs. An update asks only for the inputs the new version reported
// missing. Mounted while open so it resets each time.
function InstallDialog({
  mode,
  bundleId,
  name,
  choices,
  sourceChoice,
  fields,
  missing,
  onOpenChange,
  onSubmit,
  submitting,
}: {
  mode: "install" | "update";
  bundleId: string;
  name: string;
  choices: DeployPlatform[];
  sourceChoice: SourceChoice | null;
  fields: BundleInput[];
  missing: string[]; // keys the brain reported blank last time
  onOpenChange: (open: boolean) => void;
  onSubmit: (opts: BundleInstallOptions) => void;
  submitting: boolean;
}) {
  const [platform, setPlatform] = useState<DeployPlatform | undefined>(choices[0]);
  const [source, setSource] = useState<BundleSource | undefined>(sourceChoice?.defaultSource);
  const [repository, setRepository] = useState(`boo-${bundleId}`);
  const [editingRepo, setEditingRepo] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [attempted, setAttempted] = useState(false);
  const repoName = repository.trim();
  const repoInvalid = source === "repository" && (repoName === "." || repoName === ".." || !REPO_NAME_RE.test(repoName));
  const blank = (key: string) => !(values[key] ?? "").trim();
  const fieldError = (f: BundleInput) => f.required && blank(f.key) && (attempted || missing.includes(f.key));
  const inputsIncomplete = fields.some((f) => f.required && blank(f.key));

  const description =
    mode === "update"
      ? "This version needs a few more details before it can finish updating."
      : choices.length > 1 && sourceChoice
        ? "Choose where this app runs and where its source lives."
        : choices.length > 1
          ? "Choose where this app runs."
          : sourceChoice
            ? "Choose where this app's source lives."
            : "Add a few details to set it up.";

  const submit = () => {
    setAttempted(true);
    if (inputsIncomplete || repoInvalid) return;
    // A blank optional input is left out so the brain applies its default.
    const inputs = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim()));
    onSubmit({
      platform,
      source,
      connectionId: source === "repository" ? sourceChoice?.connectionId : undefined,
      repository: source === "repository" ? repoName : undefined,
      inputs: Object.keys(inputs).length ? inputs : undefined,
    });
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "update" ? "Update" : "Enable"} {name}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {choices.length > 1 && (
          <RadioGroup
            value={platform}
            onValueChange={(v) => setPlatform(v as DeployPlatform)}
            className="gap-3"
          >
            {choices.includes("railway") && (
              <div className="flex items-start gap-2">
                <RadioGroupItem value="railway" id="pp-railway" className="mt-0.5" />
                <Label htmlFor="pp-railway" className="font-normal leading-snug">
                  Boo-hosted (default) — a private URL on your Boo domain.
                </Label>
              </div>
            )}
            {choices.includes("gcp") && (
              <div className="flex items-start gap-2">
                <RadioGroupItem value="gcp" id="pp-gcp" className="mt-0.5" />
                <Label htmlFor="pp-gcp" className="font-normal leading-snug">
                  Your Google Cloud — runs in your own project, gated by Google
                  sign-in to your Workspace domain.
                </Label>
              </div>
            )}
          </RadioGroup>
        )}

        {sourceChoice && (
          <div className="space-y-2">
            {choices.length > 1 && <div className="text-xs font-medium">Source</div>}
            <RadioGroup
              value={source}
              onValueChange={(v) => setSource(v as BundleSource)}
              className="gap-3"
            >
              <div className="flex items-start gap-2">
                <RadioGroupItem value="repository" id="src-repository" className="mt-0.5" />
                <Label htmlFor="src-repository" className="font-normal leading-snug">
                  {sourceChoice.accountLogin}
                  {sourceChoice.instance ? ` on ${sourceChoice.instance}` : ""} — a private {repoNoun(sourceChoice.host)} per app.
                </Label>
              </div>
              {source === "repository" &&
                (editingRepo ? (
                  <div className="space-y-1.5 pl-6">
                    <Label htmlFor="src-repository-name" className="text-xs">
                      Repository name
                    </Label>
                    <div className="flex items-center rounded-md border bg-background focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
                      <span className="pl-3 pr-0.5 text-sm text-muted-foreground">{sourceChoice.accountLogin}/</span>
                      <Input
                        id="src-repository-name"
                        autoFocus
                        value={repository}
                        onChange={(e) => setRepository(e.target.value)}
                        className="border-0 pl-0 shadow-none focus-visible:ring-0"
                        aria-invalid={repoInvalid}
                      />
                    </div>
                    {repoInvalid ? (
                      <p className="text-xs text-destructive">
                        Use letters, digits, “.”, “_” or “-”, up to 100 characters.
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">A new private repository. It can't be one that already exists.</p>
                    )}
                  </div>
                ) : (
                  <p className="pl-6 text-xs text-muted-foreground">
                    {sourceChoice.accountLogin}/{repoName}
                    <span aria-hidden> · </span>
                    <button
                      type="button"
                      className="text-foreground underline-offset-2 hover:underline"
                      onClick={() => setEditingRepo(true)}
                    >
                      Change name
                    </button>
                  </p>
                ))}
              {source === "repository" && sourceChoice.selectedOnly && (
                <div className="pl-6">
                  <SelectedReposWarning manageUrl={sourceChoice.manageUrl} />
                </div>
              )}
              <div className="flex items-start gap-2">
                <RadioGroupItem value="managed" id="src-managed" className="mt-0.5" />
                <Label htmlFor="src-managed" className="font-normal leading-snug">
                  Boo-hosted — Boo keeps the source.
                </Label>
              </div>
            </RadioGroup>
          </div>
        )}

        {fields.length > 0 && (
          <div className="flex flex-col gap-4">
            {fields.map((f) => {
              const id = `bundle-input-${f.key}`;
              const invalid = fieldError(f);
              return (
                <div key={f.key} className="space-y-1.5">
                  <Label htmlFor={id}>
                    {f.name}
                    {!f.required && <span className="font-normal text-muted-foreground">(optional)</span>}
                  </Label>
                  <Input
                    id={id}
                    type={f.secret ? "password" : "text"}
                    autoComplete="off"
                    placeholder={f.example ?? f.default}
                    value={values[f.key] ?? ""}
                    onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    aria-invalid={invalid}
                    aria-describedby={`${id}-help`}
                  />
                  <p id={`${id}-help`} className={cn("text-xs text-pretty", invalid ? "text-destructive" : "text-muted-foreground")}>
                    {invalid ? `${f.name} is required.` : f.description}
                    {!invalid && !f.required && f.default && ` Leave blank to use ${f.default}.`}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting || repoInvalid}>
            {submitting && <Loader2 className="size-3.5 mr-1.5 animate-spin" />}
            {mode === "update" ? "Update" : "Enable"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Configures the org's Google Cloud deploy platform once. After this, any app
// that supports GCP can be enabled there. Mounted while open so it resets.
function GcpPlatformDialog({
  onOpenChange,
  onSubmit,
  submitting,
}: {
  onOpenChange: (open: boolean) => void;
  onSubmit: (body: {
    serviceAccount: string;
    projectId: string;
    region: string;
    iapDomain: string;
  }) => void;
  submitting: boolean;
}) {
  const [serviceAccount, setServiceAccount] = useState("");
  const [projectId, setProjectId] = useState("");
  const [region, setRegion] = useState("us-central1");
  const [iapDomain, setIapDomain] = useState("");

  const saMalformed = (() => {
    const v = serviceAccount.trim();
    if (v === "") return false;
    try {
      JSON.parse(v);
      return false;
    } catch {
      return true;
    }
  })();

  const incomplete =
    serviceAccount.trim() === "" ||
    projectId.trim() === "" ||
    region.trim() === "" ||
    iapDomain.trim() === "";

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect Google Cloud</DialogTitle>
          <DialogDescription>
            Apps you enable on Google Cloud deploy to your own project, gated by
            Google sign-in to your Workspace domain.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="gcp-sa">Deploy service-account key (JSON)</Label>
            <Textarea
              id="gcp-sa"
              rows={4}
              value={serviceAccount}
              onChange={(e) => setServiceAccount(e.target.value)}
              placeholder='{"type":"service_account",…}'
              className="font-mono text-xs"
            />
            {saMalformed && (
              <p className="text-xs text-destructive">
                Not valid JSON — paste the full service-account key file.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="gcp-project">Project id</Label>
              <Input
                id="gcp-project"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                placeholder="my-project-123"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="gcp-region">Region</Label>
              <Input
                id="gcp-region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="us-central1"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="gcp-iap-domain">Allowed Google Workspace domain</Label>
            <Input
              id="gcp-iap-domain"
              value={iapDomain}
              onChange={(e) => setIapDomain(e.target.value)}
              placeholder="example.com"
            />
            <p className="text-xs text-muted-foreground">
              Anyone signing in with a Google account on this domain can open the
              deployed apps; access is granted automatically on deploy.
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            Requires a one-time IAP setup on your project first.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onSubmit({
                serviceAccount: serviceAccount.trim(),
                projectId: projectId.trim(),
                region: region.trim(),
                iapDomain: iapDomain.trim(),
              })
            }
            disabled={submitting || incomplete || saMalformed}
          >
            {submitting && <Loader2 className="size-3.5 mr-1.5 animate-spin" />}
            Connect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Built-in capabilities have a switch, not Enable/Disable, so their toasts say
// what the switch did.
type Wording = "store" | "capability";

// One toast per finished operation, named the way its button was ("Update" →
// "Updated"). Compares consecutive server responses, so optimistic cache
// writes never trigger one; a started operation is recorded as the previous
// status, so one that finishes before the next poll still announces.
function announce(prev: BundleInstallStatus | undefined, bundle: BundleCatalogEntry, wording: Wording) {
  const { status, upgrade, missingInputs } = bundle.install;
  if (wording === "capability") {
    if ((prev === "pending" || prev === "installing" || prev === "error") && status === "installed") {
      toast.success(`Turned on ${bundle.name}`);
      return;
    }
    if ((prev === "pending" || prev === "installing") && status === "error") {
      toast.error(`Couldn't turn on ${bundle.name}`);
      return;
    }
    if (prev === "uninstalling" && (status === "not_installed" || status === "uninstalled")) {
      toast.success(`Turned off ${bundle.name}`);
      return;
    }
    if (prev === "uninstalling" && status === "error") {
      toast.error(`Couldn't turn off ${bundle.name}`);
      return;
    }
  }
  if (prev === "upgrading" && status !== "upgrading") {
    if (status !== "installed") toast.error(`${bundle.name} isn't enabled on this Boo anymore`);
    else if (upgrade.unconfirmed) toast(`Still confirming the ${bundle.name} update`);
    else if (missingInputs.length) toast(`${bundle.name} needs more details to finish updating`);
    else if (upgrade.error) toast.error(`Couldn't update ${bundle.name}`);
    else if (upgrade.conflicts.length) {
      toast.success(
        `Updated ${bundle.name}. Boo is reconciling ${plural(upgrade.conflicts.length, "customized area", "customized areas")}.`,
      );
    } else toast.success(`Updated ${bundle.name}`);
  }
  // "error" → "installed" is a retried install that finished.
  if ((prev === "pending" || prev === "installing" || prev === "error") && status === "installed") {
    toast.success(`Enabled ${bundle.name}`);
  }
  if ((prev === "pending" || prev === "installing") && status === "error") toast.error(`Couldn't enable ${bundle.name}`);
}

function RowSkeleton() {
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <Skeleton className="size-10 rounded-[22%]" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-3.5 w-32" />
        <Skeleton className="h-3 w-56" />
      </div>
      <Skeleton className="h-8 w-20 rounded-full" />
    </li>
  );
}

// Quick at first, slower once it's clearly a long one.
const ATTACH_POLL_MS = 2000;
const ATTACH_SLOW_POLL_MS = 5000;
const ATTACH_SLOW_AFTER_MS = 60_000;
// An attach runs app by app and can queue behind an install for up to 30
// minutes. Past this cap polling stops and the rows keep reading as setting
// up, with no error. Lists without `domainAttaching` can't say it's still
// running, so they stop sooner.
const ATTACH_CAP_MS = 35 * 60_000;
const ATTACH_CAP_UNTRACKED_MS = 45_000;
// Polls before a pending row never seen attaching counts as not started.
const ATTACH_GRACE_POLLS = 3;

// "Try again" for web addresses. The backend attaches every pending address on
// the Boo in the background (or is already doing so), so this polls the list
// until the rows it was waiting on stop attaching, then says how it went.
function useAddressRetry(
  containerId: string,
  refetch: () => Promise<{ data?: { data: BundleCatalogEntry[] } }>,
  rows: BundleCatalogEntry[],
  attach: (containerId: string) => Promise<{ bundles?: string[] }>,
  bumpRerun: (ids: readonly string[]) => Promise<void>,
) {
  const [waiting, setWaiting] = useState<Map<string, string | null> | null>(null);
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  });
  // `alreadyRunning` is the same as starting one: wait for it.
  const start = useMutation({
    mutationFn: () => attach(containerId),
    // A Boo that reconciles its bundles re-runs the apps it names; their rows
    // read "Retrying…" until it has, so there's nothing to wait on here.
    onSuccess: async (res) => {
      if (res.bundles) {
        if (res.bundles.length === 0) toast("Every app already has its web address");
        else await bumpRerun(res.bundles);
        return;
      }
      setWaiting(
        new Map(rows.filter((b) => addressPending(b) || addressAttaching(b)).map((b) => [b.id, b.install.domainError])),
      );
    },
    onError: () => toast.error("Couldn't set up the web address. Try again later."),
  });

  useEffect(() => {
    if (!waiting) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const started = Date.now();
    const seen = new Set<string>();
    let polls = 0;
    const tick = async () => {
      const res = await refetchRef.current();
      if (stopped) return;
      polls++;
      const list = res.data?.data ?? [];
      const outcome = settleAddresses(waiting, list, seen, polls >= ATTACH_GRACE_POLLS);
      const tracked = list.some((b) => b.install.domainAttaching !== undefined);
      const elapsed = Date.now() - started;
      if (outcome === "waiting") {
        // Past the cap: stop polling but keep "Setting up…", never an error.
        if (elapsed < (tracked ? ATTACH_CAP_MS : ATTACH_CAP_UNTRACKED_MS)) {
          timer = setTimeout(tick, elapsed < ATTACH_SLOW_AFTER_MS ? ATTACH_POLL_MS : ATTACH_SLOW_POLL_MS);
        }
        return;
      }
      setWaiting(null);
      if (outcome === "ready") toast.success("Web address ready");
      else toast.error("Couldn't set up the web address. Try again later.");
    };
    timer = setTimeout(tick, ATTACH_POLL_MS);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [waiting]);

  // A row reads as setting up while its attach runs, whoever started it.
  const settingUp = (b: BundleCatalogEntry) => addressAttaching(b) || start.isPending || !!waiting?.has(b.id);
  return { retry: () => start.mutate(), settingUp };
}

// The quiet line on a row (and in its details) while its address is pending.
function AddressNote({ canRetry, busy, onRetry }: { canRetry: boolean; busy: boolean; onRetry: () => void }) {
  return (
    <span className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
      {busy ? (
        <span className="inline-flex items-center gap-1.5">
          <Loader2 className="size-3 animate-spin" />
          Setting up its web address…
        </span>
      ) : (
        <>
          <span>Still setting up its web address</span>
          {canRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="font-medium text-foreground underline underline-offset-2 outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
            >
              Try again
            </button>
          )}
        </>
      )}
    </span>
  );
}

// The bundle list for one Boo, polled while anything is mid-flight, plus
// optimistic status patches. The App store and the built-in capabilities each
// mount it, never both at once (they sit on different sub-tabs).
function useBundleList(containerId: string, admin: boolean, wording: Wording = "store") {
  const queryClient = useQueryClient();
  const bundleApi = admin ? api.admin.containerBundles : api.containers.bundles;
  const scope = admin ? "admin" : "org";
  const listKey = ["bundles", scope, containerId];
  const lastStatuses = useRef(new Map<string, BundleInstallStatus>());
  const lastReconciles = useRef(new Map<string, BundleCatalogEntry["install"]["reconcile"]>());

  const listQuery = useQuery({
    queryKey: listKey,
    queryFn: async () => {
      const raw = await bundleApi.list(containerId);
      // Phases are this Boo's only when it reconciles its bundles; otherwise
      // the rows read from the install operations, as before.
      const res =
        raw.reconciler === 1 ? raw : { ...raw, data: raw.data.map((b) => ({ ...b, install: { ...b.install, reconcile: null } })) };
      const prev = lastStatuses.current;
      for (const b of res.data) {
        if (b.install.reconcile) {
          const said = reconcileAnnouncement(lastReconciles.current.get(`${containerId}:${b.id}`), b);
          if (said) (said.tone === "error" ? toast.error : toast.success)(said.text);
        } else {
          announce(prev.get(`${containerId}:${b.id}`), b, wording);
        }
      }
      lastStatuses.current = new Map(res.data.map((b) => [`${containerId}:${b.id}`, b.install.status]));
      lastReconciles.current = new Map(res.data.map((b) => [`${containerId}:${b.id}`, b.install.reconcile]));
      return res;
    },
    // Keep polling while anything is provisioning, or while the brain is
    // still starting up / unreachable (409/404) so the section fills in on
    // its own once it's ready.
    refetchInterval: (query) => {
      if (isNotReady(query.state.error)) return 5000;
      const poll = listPoll(query.state.data?.data ?? []);
      return poll === "soon" ? POLL_MS : poll === "later" ? RECONCILE_WAIT_POLL_MS : false;
    },
  });

  // Optimistically patch the row so polling kicks in immediately and the
  // button shows its spinner without waiting for the next refetch.
  function patchInstall(id: string, patch: (b: BundleCatalogEntry) => BundleCatalogEntry["install"]) {
    queryClient.setQueryData<typeof listQuery.data>(listKey, (prev) =>
      prev ? { ...prev, data: prev.data.map((b) => (b.id === id ? { ...b, install: patch(b) } : b)) } : prev,
    );
  }
  const markInstall = (id: string, status: BundleInstallStatus, record = true) => {
    if (record) lastStatuses.current.set(`${containerId}:${id}`, status);
    patchInstall(id, (b) => ({ ...b.install, status }));
  };
  const lastStatus = (id: string) => lastStatuses.current.get(`${containerId}:${id}`);
  const synced = listQuery.data?.reconciler === 1;

  // Retry and Repair: the Boo runs the bundle again once it sees the counter
  // move, so the row reads "Retrying…" until it reports it did.
  // A poll already in flight would overwrite the bump, so it is cancelled.
  const bumpRerun = async (ids: readonly string[]) => {
    await queryClient.cancelQueries({ queryKey: listKey });
    for (const id of ids) patchInstall(id, (b) => ({ ...b.install, rerun: (b.install.rerun ?? 0) + 1 }));
  };
  const retry = useMutation({
    mutationFn: (id: string) => bundleApi.retry(containerId, id),
    onSuccess: (_res, id) => bumpRerun([id]),
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't retry"),
  });

  return { bundleApi, scope, listQuery, patchInstall, markInstall, lastStatus, synced, retry, bumpRerun };
}

// Per-brain apps (bundles) that run on this brain. Brain-only, rendered by
// AppsTab for `boo-brain` containers. Permissions come from the bundle
// endpoints' own `_permissions`, not the container's.
//
// `admin` swaps the org-scoped /api/containers/:id/bundles routes for the
// site-admin /api/admin/containers/:id/bundles twins, so this same UI can
// manage a parked (parked-org) brain the admin isn't a member of. Query keys
// are scoped so the two modes never share a cache entry.
export function Bundles({
  containerId,
  admin = false,
  details: openId,
  onDetailsChange,
}: {
  containerId: string;
  admin?: boolean;
  // Which app's details are open, when the caller keeps it (in the URL).
  details?: string | null;
  onDetailsChange?: (id: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState<BundleCatalogEntry | null>(null);
  const [localDetailsId, setLocalDetailsId] = useState<string | null>(null);
  const detailsId = onDetailsChange ? (openId ?? null) : localDetailsId;
  const setDetailsId = onDetailsChange ?? setLocalDetailsId;
  const [category, setCategory] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{
    mode: "install" | "update";
    id: string;
    name: string;
    choices: DeployPlatform[];
    sourceChoice: SourceChoice | null;
    fields: BundleInput[];
    missing: string[];
  } | null>(null);
  const { bundleApi, scope, listQuery, patchInstall, markInstall, lastStatus, synced, retry, bumpRerun } = useBundleList(containerId, admin);

  const configQuery = useQuery({
    queryKey: ["bundles-config", scope, containerId],
    queryFn: () => bundleApi.config(containerId),
    refetchInterval: (query) => {
      const s = query.state.data?.data.status;
      return s === "pending" || s === "configuring" ? POLL_MS : false;
    },
  });

  const platformsQuery = useQuery({
    queryKey: ["deploy-platforms", scope, containerId],
    queryFn: () => bundleApi.deployPlatforms(containerId),
  });
  const configuredPlatforms = platformsQuery.data?.data.platforms ?? ["railway"];

  const perms: Permissions = listQuery.data?._permissions ?? configQuery.data?._permissions ?? null;
  const canInstall = perms?.canInstall === true;
  const canUninstall = perms?.canUninstall === true;
  const canConfigure = perms?.canConfigure === true;
  const address = useAddressRetry(containerId, listQuery.refetch, listQuery.data?.data ?? [], bundleApi.attachDomains, bumpRerun);
  const addressNote = (bundle: BundleCatalogEntry) =>
    addressPending(bundle) || addressAttaching(bundle) ? (
      <AddressNote canRetry={canConfigure} busy={address.settingUp(bundle)} onRetry={address.retry} />
    ) : null;

  // The mutations return 202 before the backend has committed the in-flight
  // status, so we don't invalidate here: a refetch could race the write and
  // revert to the terminal state, stopping the poll. The optimistic flip both
  // shows the spinner and arms refetchInterval; the 3.5s poll reconciles once
  // the background claim has landed.
  // Whether this Boo can keep an app's source in a code host (org scope only).
  const { active: codeHosts, defaultConnection } = useCodeHosts(containerId, { enabled: !admin });

  const install = useMutation({
    mutationFn: (v: { id: string; opts: BundleInstallOptions }) =>
      admin
        ? api.admin.containerBundles.install(containerId, v.id, { platform: v.opts.platform, inputs: v.opts.inputs })
        : api.containers.bundles.install(containerId, v.id, v.opts),
    // The install claims its row only after this 202, so a poll can still
    // return a retried install's old "error". Keep "error" as the previous
    // status then, or that stale poll would toast as a new failure.
    onSuccess: (_res, v) => markInstall(v.id, "installing", lastStatus(v.id) !== "error"),
    onError: (err) => toast.error(err instanceof Error ? err.message : "Enable failed"),
  });

  // The backend claims the upgrade before answering, so the row is already
  // "upgrading" server-side and polling can't flip it back.
  const upgrade = useMutation({
    mutationFn: (v: { id: string; inputs?: Record<string, string> }) => bundleApi.upgrade(containerId, v.id, v.inputs),
    onSuccess: (res, v) => {
      // A Boo that updates its apps itself keeps the inputs and carries on.
      if (res.status === "automatic") {
        patchInstall(v.id, (b) => ({ ...b.install, missingInputs: [] }));
        const name = listQuery.data?.data.find((b) => b.id === v.id)?.name ?? "this app";
        toast.success(`Saved. Boo will carry on setting up ${name}.`);
        void listQuery.refetch();
        return;
      }
      markInstall(v.id, "upgrading");
      patchInstall(v.id, (b) => ({
        ...b.install,
        status: "upgrading",
        missingInputs: [],
        upgrade: { ...b.install.upgrade, target: res.target ?? null, stage: null, error: null, unconfirmed: false },
      }));
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Update failed"),
  });

  // A bundle asks before installing when it supports more than one of the
  // platforms this org has configured, when it is an app (has platforms) on a
  // Boo connected to GitHub (source choice), or when it declares inputs. With
  // none of those it installs one-click on the single available platform
  // (default Boo-hosted), and the brain picks the source from the Boo's
  // app-source setting.
  const onInstall = (bundle: BundleCatalogEntry) => {
    const choices = (bundle.platforms ?? []).filter((p) => configuredPlatforms.includes(p));
    // The default connection, or the only one, is where a repository would go.
    const target = defaultConnection ?? (codeHosts.length === 1 ? codeHosts[0] : null);
    const sourceChoice =
      bundle.platforms?.length && target
        ? {
            host: target.host,
            hostLabel: target.hostLabel,
            instance: instanceLabel(target),
            connectionId: target.id,
            accountLogin: target.namespace,
            defaultSource: (defaultConnection ? "repository" : "managed") as BundleSource,
            selectedOnly: target.host === "github" && target.repositorySelection === "selected",
            manageUrl: target.manageUrl,
          }
        : null;
    if (choices.length > 1 || sourceChoice || bundle.inputs.length) {
      setDialog({
        mode: "install",
        id: bundle.id,
        name: bundle.name,
        choices,
        sourceChoice,
        fields: bundle.inputs,
        missing: bundle.install.missingInputs,
      });
    } else {
      install.mutate({ id: bundle.id, opts: { platform: choices[0] } });
    }
  };

  // An update normally runs as-is; it asks only for inputs the new version
  // reported missing on a previous attempt.
  // The inputs a reconciled bundle waits on: every required one it declares.
  const onAddDetails = (bundle: BundleCatalogEntry) => {
    const fields = bundle.inputs.filter((i) => i.required);
    setDialog({ mode: "update", id: bundle.id, name: bundle.name, choices: [], sourceChoice: null, fields, missing: fields.map((i) => i.key) });
  };

  const onUpdate = (bundle: BundleCatalogEntry) => {
    const missing = bundle.install.missingInputs;
    const fields = bundle.inputs.filter((i) => missing.includes(i.key));
    if (fields.length) {
      setDialog({ mode: "update", id: bundle.id, name: bundle.name, choices: [], sourceChoice: null, fields, missing });
    } else {
      upgrade.mutate({ id: bundle.id });
    }
  };

  const uninstall = useMutation({
    mutationFn: (id: string) => bundleApi.uninstall(containerId, id),
    onSuccess: (_res, id) => {
      markInstall(id, "uninstalling");
      setRemoving(null);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Disable failed");
      setRemoving(null);
    },
  });

  const configure = useMutation({
    mutationFn: () => bundleApi.configure(containerId),
    onSuccess: () => {
      queryClient.setQueryData<typeof configQuery.data>(["bundles-config", scope, containerId], (prev) =>
        prev ? { ...prev, data: { ...prev.data, status: "configuring", error: null } } : prev,
      );
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Setup failed"),
  });

  const listErr = listQuery.error;
  // Only surface the not-ready state when we have nothing to show. If a prior
  // load succeeded, a transient 409/404 during polling keeps the last list
  // rather than wiping it (per the integration contract: keep prior state).
  const notReady = isNotReady(listErr) && !listQuery.data;
  const configStatus = configQuery.data?.data.status;

  const all = listQuery.data?.data ?? [];
  // Built-in capabilities live under App configuration > Advanced. Staff
  // (admin) manage every bundle here.
  const rows = admin ? all : all.filter((b) => !isCoreBundle(b));
  // The brain runs one upgrade at a time and nothing else alongside it, so an
  // upgrade blocks every action, and any operation blocks starting an upgrade.
  // A Boo that reconciles its bundles queues what it's asked, so nothing waits.
  const upgradeRunning = !synced && (all.some((b) => b.install.status === "upgrading") || upgrade.isPending);
  const anyInFlight = all.some((b) => INSTALL_IN_FLIGHT.has(b.install.status)) || install.isPending || uninstall.isPending;
  // The category filter narrows only the catalog; what's on this Boo stays whole.
  const onBoo = rows.filter((b) => ON_BOO.has(b.install.status));
  const catalog = rows.filter((b) => !ON_BOO.has(b.install.status));
  const categories = orderCategories(catalog.flatMap((b) => (b.category ? [b.category] : [])));
  const activeCategory = category && categories.includes(category) ? category : null;
  const available = activeCategory ? catalog.filter((b) => b.category === activeCategory) : catalog;
  const details = detailsId ? rows.find((b) => b.id === detailsId) ?? null : null;

  const pendingLabel = (id: string) =>
    retry.isPending && retry.variables === id
      ? "Retrying…"
      : install.isPending && install.variables?.id === id
      ? "Enabling…"
      : upgrade.isPending && upgrade.variables?.id === id
        ? "Updating…"
        : uninstall.isPending && uninstall.variables === id
          ? "Disabling…"
          : null;

  // In the sheet Enable/Update is the primary button; the app's own link
  // lives in the sheet's Address row.
  const sheetActions = (bundle: BundleCatalogEntry) => {
    const action = rowAction(bundle, pendingLabel(bundle.id));
    const isUpdate = action.kind === "update" || action.kind === "retry-update";
    return (
      <ActionButton
        action={action}
        canInstall={canInstall}
        blocked={upgradeRunning || (isUpdate && anyInFlight)}
        prominent
        onEnable={() => onInstall(bundle)}
        onUpdate={() => onUpdate(bundle)}
        onRetry={() => retry.mutate(bundle.id)}
        onAddDetails={() => onAddDetails(bundle)}
      />
    );
  };

  const installedTrailing = (bundle: BundleCatalogEntry): ReactNode => {
    const action = rowAction(bundle, pendingLabel(bundle.id));
    const busy = action.kind === "busy" ? action.label : null;
    if (busy) {
      return (
        <span className="inline-flex items-center gap-1.5 pr-1 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          {busy}
        </span>
      );
    }
    const url = bundle.kind === "app" && !addressPending(bundle) && !addressAttaching(bundle) ? bundle.install.url : null;
    if (url) {
      return (
        <RowIconButton label={`Open ${bundle.name} in a new tab`} tooltip={url.replace(/^https?:\/\//, "")}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <ArrowUpRight className="size-4" />
          </a>
        </RowIconButton>
      );
    }
    return null;
  };
  const pendingUpdates = countPendingUpdates(onBoo);

  const renderRows = (list: BundleCatalogEntry[]) =>
    list.map((bundle) => (
      <BundleRow
        key={bundle.id}
        bundle={bundle}
        trailing={installedTrailing(bundle)}
        note={addressNote(bundle)}
        onOpenDetails={() => setDetailsId(bundle.id)}
      />
    ));

  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-1">
        {admin && <h2 className="text-base font-semibold text-balance">Apps</h2>}
        <p className="text-sm text-muted-foreground text-pretty">
          Add apps to your Boo.
          {!canInstall && " You can see what's enabled but not change it."}
        </p>
      </div>

      {configStatus === "error" && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 text-xs text-destructive">
            <TriangleAlert className="size-4 shrink-0" />
            <span>
              Setup didn’t finish{configQuery.data?.data.error ? `: ${configQuery.data.data.error}` : "."}
            </span>
          </div>
          {canConfigure && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => configure.mutate()}
              disabled={configure.isPending}
            >
              {configure.isPending && <Loader2 className="size-3.5 mr-1.5 animate-spin" />}
              Retry setup
            </Button>
          )}
        </div>
      )}

      {listQuery.isLoading ? (
        <ul aria-busy className="divide-y rounded-xl border bg-card">
          <RowSkeleton />
          <RowSkeleton />
          <RowSkeleton />
        </ul>
      ) : notReady ? (
        <div className="rounded-lg border p-6 text-center text-sm text-muted-foreground">
          This Boo isn’t ready yet. Apps will be available once it’s running.
        </div>
      ) : listQuery.isError && !listQuery.data ? (
        <InlineError error={listErr} />
      ) : rows.length === 0 ? (
        <div className="rounded-lg border p-6 text-center text-sm text-muted-foreground">
          No apps available yet.
        </div>
      ) : (
        <>
          {onBoo.length > 0 && (
            <BundleSection
              title="On this Boo"
              layout="list"
              aside={
                pendingUpdates > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {plural(pendingUpdates, "update", "updates")} available
                  </span>
                )
              }
            >
              {renderRows(onBoo)}
            </BundleSection>
          )}
          {catalog.length > 0 && (
            <BundleSection
              title="Available"
              layout="grid"
              toolbar={
                categories.length > 1 && (
                  <ChipScroller label="Filter by category">
                    {[null, ...categories].map((c) => {
                      const selected = c === activeCategory;
                      return (
                        <button
                          key={c ?? "all"}
                          type="button"
                          aria-pressed={selected}
                          onClick={(e) => {
                            setCategory(c);
                            e.currentTarget.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
                          }}
                          className={cn(
                            "h-8 shrink-0 whitespace-nowrap rounded-full px-3 text-xs font-medium outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/50",
                            selected ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:text-foreground",
                          )}
                        >
                          {c ? categoryLabel(c) : "All"}
                        </button>
                      );
                    })}
                  </ChipScroller>
                )
              }
            >
              {available.map((bundle) => (
                <BundleTile
                  key={bundle.id}
                  bundle={bundle}
                  pendingLabel={pendingLabel(bundle.id)}
                  onOpenDetails={() => setDetailsId(bundle.id)}
                />
              ))}
            </BundleSection>
          )}
        </>
      )}

      {/* The org view shows deploy platforms under App configuration. */}
      {admin && <DeployPlatforms containerId={containerId} admin />}

      {details && (
        <BundleSheet
          bundle={details}
          action={sheetActions(details)}
          canUninstall={canUninstall}
          blocked={upgradeRunning || (!synced && INSTALL_IN_FLIGHT.has(details.install.status))}
          onDisable={() => setRemoving(details)}
          onClose={() => setDetailsId(null)}
          addressNote={addressNote(details)}
        />
      )}

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={removing ? `Disable ${removing.name}?` : "Disable app?"}
        description="This disables it on your Boo. You can enable it again later."
        confirmLabel="Disable"
        loading={uninstall.isPending}
        onConfirm={() => removing && uninstall.mutate(removing.id)}
      />

      {dialog && (
        <InstallDialog
          mode={dialog.mode}
          bundleId={dialog.id}
          name={dialog.name}
          choices={dialog.choices}
          sourceChoice={dialog.sourceChoice}
          fields={dialog.fields}
          missing={dialog.missing}
          onOpenChange={(open) => !open && setDialog(null)}
          submitting={
            dialog.mode === "update"
              ? upgrade.isPending && upgrade.variables?.id === dialog.id
              : install.isPending && install.variables?.id === dialog.id
          }
          onSubmit={(opts) => {
            if (dialog.mode === "update") upgrade.mutate({ id: dialog.id, inputs: opts.inputs });
            else install.mutate({ id: dialog.id, opts });
            setDialog(null);
          }}
        />
      )}
    </div>
  );
}

// Where this Boo's apps can run: Boo-hosted always, and the org's own Google
// Cloud once connected. Only people who can configure apps see it.
export function DeployPlatforms({ containerId, admin = false }: { containerId: string; admin?: boolean }) {
  const queryClient = useQueryClient();
  const [gcpDialogOpen, setGcpDialogOpen] = useState(false);
  const bundleApi = admin ? api.admin.containerBundles : api.containers.bundles;
  const scope = admin ? "admin" : "org";
  const platformsQuery = useQuery({
    queryKey: ["deploy-platforms", scope, containerId],
    queryFn: () => bundleApi.deployPlatforms(containerId),
  });
  const configureGcp = useMutation({
    mutationFn: (body: { serviceAccount: string; projectId: string; region: string; iapDomain: string }) =>
      bundleApi.configureGcpPlatform(containerId, body),
    onSuccess: () => {
      setGcpDialogOpen(false);
      toast.success("Google Cloud connected");
      queryClient.invalidateQueries({ queryKey: ["deploy-platforms", scope, containerId] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Connect failed"),
  });

  if (platformsQuery.data?._permissions?.canConfigure !== true) return null;
  const gcpConfigured = platformsQuery.data.data.platforms.includes("gcp");

  return (
    <section className="flex flex-col gap-3">
      <div className="space-y-1">
        <h3 className="text-sm font-medium">Where apps run</h3>
        <p className="text-sm text-muted-foreground text-pretty">Apps run on Boo's hosting unless you connect Google Cloud.</p>
      </div>
      <ul className="divide-y rounded-xl border bg-card">
        <li className="flex items-center gap-3 px-4 py-3">
          <Cloud className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className="text-sm font-medium">Google Cloud</span>
              {gcpConfigured && (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Check className="size-3.5" />
                  Connected
                </span>
              )}
            </span>
            <span className="block text-xs text-muted-foreground text-pretty">
              Your own project, gated by Google sign-in to your Workspace domain.
            </span>
          </span>
          <Button size="sm" variant="outline" className="shrink-0" onClick={() => setGcpDialogOpen(true)}>
            {gcpConfigured ? "Reconnect" : "Connect"}
          </Button>
        </li>
      </ul>
      {gcpDialogOpen && (
        <GcpPlatformDialog
          onOpenChange={setGcpDialogOpen}
          submitting={configureGcp.isPending}
          onSubmit={(body) => configureGcp.mutate(body)}
        />
      )}
    </section>
  );
}

// The bundles built into this Boo (core in their manifest), each with an on/off
// switch. Off uninstalls the bundle, on installs it with its defaults. `orgId`
// builds the links to an app in the App store.
// The store's busy words, as a built-in capability's switch says them.
const CAPABILITY_BUSY: Record<string, string> = {
  "Enabling…": "Turning on…",
  "Disabling…": "Turning off…",
};

export function BuiltInCapabilities({ containerId, orgId }: { containerId: string; orgId: string }) {
  const { bundleApi, listQuery, markInstall, lastStatus, synced, retry, bumpRerun } = useBundleList(containerId, false, "capability");
  const [turningOff, setTurningOff] = useState<BundleCatalogEntry | null>(null);

  // A built-in app's code stays with Boo: turning it on never creates a
  // repository in the customer's code host, whatever the Boo's default.
  const install = useMutation({
    mutationFn: (bundle: BundleCatalogEntry) =>
      bundleApi.install(containerId, bundle.id, bundle.kind === "app" ? { source: "managed" } : {}),
    onSuccess: (_res, bundle) => markInstall(bundle.id, "installing", lastStatus(bundle.id) !== "error"),
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't turn it on"),
  });
  const uninstall = useMutation({
    mutationFn: (id: string) => bundleApi.uninstall(containerId, id),
    onSuccess: (_res, id) => {
      markInstall(id, "uninstalling");
      setTurningOff(null);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Couldn't turn it off");
      setTurningOff(null);
    },
  });

  const all = listQuery.data?.data ?? [];
  const capabilities = all.filter(isCoreBundle).sort((a, b) => a.name.localeCompare(b.name));
  const perms = listQuery.data?._permissions;
  const canInstall = perms?.canInstall === true;
  const canUninstall = perms?.canUninstall === true;
  // The brain runs an upgrade alone, so one anywhere on the Boo holds every
  // switch; a Boo that reconciles its bundles queues changes instead.
  const upgradeRunning = !synced && all.some((b) => b.install.status === "upgrading");
  const address = useAddressRetry(containerId, listQuery.refetch, all, bundleApi.attachDomains, bumpRerun);

  // A Boo whose list marks nothing as built in (older Boos) has no such section.
  if (listQuery.data && capabilities.length === 0) return null;

  return (
    <section className="flex flex-col gap-3">
      <div className="space-y-1">
        <h3 className="text-sm font-medium">Built-in capabilities</h3>
        <p className="text-sm text-muted-foreground text-pretty">
          Built into your Boo. Turn one off only if your Boo shouldn't do that kind of work.
          {perms && !canInstall && !canUninstall && " You can see what's on but not change it."}
        </p>
      </div>
      {listQuery.isLoading ? (
        <ul aria-busy className="divide-y rounded-xl border bg-card">
          <RowSkeleton />
          <RowSkeleton />
        </ul>
      ) : isNotReady(listQuery.error) && !listQuery.data ? (
        <p className="text-sm text-muted-foreground">This Boo isn’t ready yet. Its capabilities show once it’s running.</p>
      ) : listQuery.isError && !listQuery.data ? (
        <InlineError error={listQuery.error} />
      ) : (
        <ul className="divide-y rounded-xl border bg-card">
          {capabilities.map((bundle) => {
            const { status } = bundle.install;
            const on = capabilityOn(status);
            const reconciled = synced ? rowAction(bundle, retry.isPending && retry.variables === bundle.id ? "Retrying…" : null) : null;
            const phaseLine = synced ? statusMessage(bundle) : null;
            const busy = reconciled
              ? reconciled.kind === "busy"
                ? CAPABILITY_BUSY[reconciled.label] ?? reconciled.label
                : (install.isPending && install.variables?.id === bundle.id)
                  ? "Turning on…"
                  : uninstall.isPending && uninstall.variables === bundle.id
                    ? "Turning off…"
                    : null
              : (install.isPending && install.variables?.id === bundle.id) || status === "pending" || status === "installing"
                ? "Turning on…"
                : (uninstall.isPending && uninstall.variables === bundle.id) || status === "uninstalling"
                  ? "Turning off…"
                  : status === "upgrading"
                    ? "Updating…"
                    : null;
            const allowed = on ? canUninstall : canInstall;
            // Locked on: its manifest says it can't be turned off, or an app
            // on the Boo needs it (the row says which).
            const fixed = on && alwaysOn(bundle);
            const heldBy = on && !fixed ? dependentsOn(bundle.id, all) : [];
            const held = fixed || heldBy.length > 0;
            const id = `capability-${bundle.id}`;
            return (
              <li key={bundle.id} className="flex items-center gap-3 px-4 py-3">
                <BundleIcon bundle={bundle} size={32} />
                <span className="min-w-0 flex-1">
                  <label htmlFor={id} className="block truncate text-sm font-medium">
                    {bundle.name}
                  </label>
                  <span className="block truncate text-xs text-muted-foreground">{bundle.tagline}</span>
                  {(addressPending(bundle) || addressAttaching(bundle)) && (
                    <AddressNote canRetry={perms?.canConfigure === true} busy={address.settingUp(bundle)} onRetry={address.retry} />
                  )}
                  {notOnBooYet(bundle) && !busy && (
                    <span className="block text-xs text-muted-foreground">Not on this Boo yet</span>
                  )}
                  {!synced && status === "error" && !busy && (
                    <span className="block text-xs text-destructive text-pretty">
                      The last change didn't finish{bundle.install.error ? `: ${bundle.install.error}` : "."}
                    </span>
                  )}
                  {phaseLine && !busy && (
                    <span className={cn("block text-xs text-pretty", phaseLine.tone === "error" ? "text-destructive" : "text-muted-foreground")}>
                      {phaseLine.text}
                    </span>
                  )}
                  {fixed && !busy && (
                    <span id={`${id}-held`} className="block text-xs text-muted-foreground">
                      Always on
                    </span>
                  )}
                  {heldBy.length > 0 && !busy && (
                    <span id={`${id}-held`} className="block text-xs text-muted-foreground text-pretty">
                      <AppLinks apps={heldBy} orgId={orgId} containerId={containerId} />{" "}
                      {heldBy.length === 1 ? "needs" : "need"} this. {STORE_REMOVE_VERB}{" "}
                      <AppLinks apps={heldBy} orgId={orgId} containerId={containerId} /> in the App store first.
                    </span>
                  )}
                </span>
                {busy && (
                  <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                    <Loader2 className="size-3.5 animate-spin" />
                    {busy}
                  </span>
                )}
                {(reconciled?.kind === "retry" || reconciled?.kind === "repair") && !busy && canInstall && (
                  <Button
                    size="sm"
                    variant="outline"
                    aria-label={`${reconciled.kind === "repair" ? "Repair" : "Retry"} ${bundle.name}`}
                    onClick={() => retry.mutate(bundle.id)}
                  >
                    {reconciled.kind === "repair" ? <Wrench className="size-3.5" /> : <RotateCw className="size-3.5" />}
                    {reconciled.kind === "repair" ? "Repair" : "Retry"}
                  </Button>
                )}
                {held && !busy && <Lock aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />}
                <Switch
                  id={id}
                  checked={on}
                  disabled={!allowed || !!busy || upgradeRunning || held}
                  aria-describedby={held && !busy ? `${id}-held` : undefined}
                  // Locked on reads as on, not as unavailable.
                  className={cn(held && !busy && "disabled:cursor-default disabled:opacity-100")}
                  onCheckedChange={(checked) => (checked ? install.mutate(bundle) : setTurningOff(bundle))}
                />
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={turningOff !== null}
        onOpenChange={(open) => !open && setTurningOff(null)}
        title={turningOff ? `Turn off ${turningOff.name}?` : "Turn off?"}
        description={turningOff ? turnOffBody(turningOff) : ""}
        confirmLabel="Turn off"
        loading={uninstall.isPending}
        onConfirm={() => turningOff && uninstall.mutate(turningOff.id)}
      />
    </section>
  );
}

// "Chief of Staff", "A and B", "A, B and C", each linking to its details in the App store.
function AppLinks({ apps, orgId, containerId }: { apps: BundleCatalogEntry[]; orgId: string; containerId: string }) {
  return (
    <>
      {apps.map((app, i) => (
        <span key={app.id}>
          {i > 0 && (i === apps.length - 1 ? " and " : ", ")}
          <Link
            to="/orgs/$orgId/containers/$containerId/apps"
            params={{ orgId, containerId }}
            search={{ tab: "store", app: app.id }}
            className="text-foreground underline underline-offset-2"
          >
            {app.name}
          </Link>
        </span>
      ))}
    </>
  );
}
